import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Search, Eye, Filter } from "lucide-react";

const COLORS = {
  primaryDark: "#08263e",
  primaryRed: "#ec474e",
  secondaryBlue: "#518dca",
};

export const SessionsTable = ({ sessions, sessionsTotal, onViewSession, onFetchSessions }) => {
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(0);

  const handleFilterChange = (setter) => (e) => {
    setter(e.target.value);
    setPage(0);
    setTimeout(() => onFetchSessions(0, { search, sourceFilter, startDate, endDate, [setter === setSearch ? 'search' : setter === setSourceFilter ? 'sourceFilter' : setter === setStartDate ? 'startDate' : 'endDate']: e.target.value }), 0);
  };

  const clearFilters = () => {
    setSearch("");
    setSourceFilter("");
    setStartDate("");
    setEndDate("");
    setPage(0);
    onFetchSessions(0, { search: "", sourceFilter: "", startDate: "", endDate: "" });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <CardTitle className="text-lg">Session History</CardTitle>
          <span className="text-sm text-[#999]">{sessionsTotal} total sessions</span>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 mt-3">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#999]" />
            <input
              type="text"
              placeholder="Search messages..."
              value={search}
              onChange={handleFilterChange(setSearch)}
              className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2"
              data-testid="session-search-input"
            />
          </div>
          <select value={sourceFilter} onChange={handleFilterChange(setSourceFilter)} className="px-3 py-2 border rounded-lg text-sm bg-white" data-testid="session-source-filter">
            <option value="">All Sources</option>
            <option value="faq">FAQ Only</option>
            <option value="llm">LLM Only</option>
          </select>
          <input type="date" value={startDate} onChange={handleFilterChange(setStartDate)} className="px-3 py-2 border rounded-lg text-sm" data-testid="session-start-date" />
          <input type="date" value={endDate} onChange={handleFilterChange(setEndDate)} className="px-3 py-2 border rounded-lg text-sm" data-testid="session-end-date" />
          {(search || sourceFilter || startDate || endDate) && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-[#999] hover:text-[#333]" data-testid="clear-filters-btn">Clear</Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 px-3">Session ID</th>
                <th className="text-left py-2 px-3">Preview</th>
                <th className="text-left py-2 px-3">Messages</th>
                <th className="text-left py-2 px-3">Tokens</th>
                <th className="text-left py-2 px-3">Cost</th>
                <th className="text-left py-2 px-3">FAQ/LLM</th>
                <th className="text-left py-2 px-3">Last Active</th>
                <th className="text-left py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {sessions.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-[#999]">
                    {search || sourceFilter || startDate || endDate ? "No sessions match your filters" : "No sessions yet"}
                  </td>
                </tr>
              ) : (
                sessions.map((session) => (
                  <tr key={session.session_id} className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => onViewSession(session.session_id)}>
                    <td className="py-2 px-3 font-mono text-xs">{session.session_id.slice(0, 8)}...</td>
                    <td className="py-2 px-3 text-xs text-[#666] max-w-[200px] truncate">{session.sample_message || "\u2014"}</td>
                    <td className="py-2 px-3">{session.message_count}</td>
                    <td className="py-2 px-3">{session.total_tokens.toLocaleString()}</td>
                    <td className="py-2 px-3">${session.total_cost.toFixed(4)}</td>
                    <td className="py-2 px-3">
                      <span className="text-green-600">{session.faq_hits}</span> / <span style={{ color: COLORS.primaryRed }}>{session.llm_calls}</span>
                    </td>
                    <td className="py-2 px-3 text-xs">{session.last_message?.slice(0, 10)}</td>
                    <td className="py-2 px-3">
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onViewSession(session.session_id); }}>
                        <Eye size={16} />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {sessionsTotal > 50 && (
          <div className="flex items-center justify-between mt-4 pt-3 border-t">
            <span className="text-sm text-[#666]">Showing {page * 50 + 1}&ndash;{Math.min((page + 1) * 50, sessionsTotal)} of {sessionsTotal}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => { setPage(p => p - 1); onFetchSessions(page - 1); }}>Previous</Button>
              <Button variant="outline" size="sm" disabled={(page + 1) * 50 >= sessionsTotal} onClick={() => { setPage(p => p + 1); onFetchSessions(page + 1); }}>Next</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
